import { c as _c } from "react/compiler-runtime";
import { ComponentProps, FocusEventHandler, FormEventHandler, MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { assertNotNullOrUndefined } from 'record-it-utils/utils/assert/assertNotNullOrUndefined';
import { StringUtils } from 'record-it-utils/utils/StringUtils/StringUtils';
import { TaskQueue } from 'record-it-utils/utils/TaskQueue/TaskQueue';
import { useEventCallback } from '../useEventCallback';
import { useIsFocused } from '../useIsFocused';
export function useFixedLengthNumberInput(options) {
  const $ = _c(30);
  const [bufferedValue, setBufferedValue] = useState(options.value !== null ? String(options.value) : "");
  const inputRef = useRef(null);
  let t0;
  if ($[0] !== options.onBlur || $[1] !== options.onFocus) {
    t0 = {
      onFocus: options.onFocus,
      onBlur: options.onBlur
    };
    $[0] = options.onBlur;
    $[1] = options.onFocus;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const {
    isFocused,
    handleBlur,
    handleFocus: handleIsFocusedFocus
  } = useIsFocused(t0);
  let t1;
  if ($[3] !== options) {
    t1 = event => {
      const {
        value
      } = event.currentTarget;
      if (value.match(/^[0-9]*$/)) {
        setBufferedValue(value);
        if (value.length >= options.valueLength) {
          options.onValueLengthReached?.();
        }
      }
    };
    $[3] = options;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const handleInput = useEventCallback(t1);
  let t2;
  if ($[5] !== handleIsFocusedFocus) {
    t2 = event_0 => {
      inputRef.current?.setSelectionRange(0, inputRef.current.value.length);
      handleIsFocusedFocus(event_0);
    };
    $[5] = handleIsFocusedFocus;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const handleFocus = useEventCallback(t2);
  let t3;
  if ($[7] !== options.value) {
    t3 = () => {
      setBufferedValue(oldValue => {
        const newValue = numberToString(options.value);
        if (newValue !== oldValue) {
          TaskQueue.queueTask(() => {
            inputRef.current?.setSelectionRange(0, inputRef.current.value.length);
          });
        }
        return newValue;
      });
    };
    $[7] = options.value;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== options.anyInputIsFocused || $[10] !== options.value) {
    t4 = [options.anyInputIsFocused, options.value];
    $[9] = options.anyInputIsFocused;
    $[10] = options.value;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  useEffect(t3, t4);
  let t5;
  if ($[12] !== bufferedValue || $[13] !== isFocused || $[14] !== options.anyInputIsFocused || $[15] !== options.value) {
    t5 = isFocused || options.anyInputIsFocused ? bufferedValue : numberToString(options.value);
    $[12] = bufferedValue;
    $[13] = isFocused;
    $[14] = options.anyInputIsFocused;
    $[15] = options.value;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  const value_0 = t5;
  let t6;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = () => {
      assertNotNullOrUndefined(inputRef.current, "can't focus when the input is not rendered");
      inputRef.current.focus();
    };
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const focus = t6;
  let t7;
  if ($[18] !== bufferedValue) {
    t7 = stringToNumber(bufferedValue);
    $[18] = bufferedValue;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== handleBlur || $[21] !== handleFocus || $[22] !== handleInput || $[23] !== options.valueLength || $[24] !== value_0) {
    t8 = {
      ref: inputRef,
      size: options.valueLength,
      value: value_0,
      onInput: handleInput,
      onBlur: handleBlur,
      onFocus: handleFocus
    };
    $[20] = handleBlur;
    $[21] = handleFocus;
    $[22] = handleInput;
    $[23] = options.valueLength;
    $[24] = value_0;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== isFocused || $[27] !== t7 || $[28] !== t8) {
    t9 = {
      isFocused,
      bufferedValue: t7,
      focus,
      inputProps: t8
    };
    $[26] = isFocused;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9;
}
export type UseFixedLengthNumberInputOptions = {
  value: number | null;
  anyInputIsFocused: boolean;
  valueLength: number;
  onFocus: FocusEventHandler<HTMLInputElement>;
  onBlur: () => void;
  onValueLengthReached?: () => void;
};
export type UseFixedLengthNumberInputReturnValue = {
  isFocused: boolean;
  bufferedValue: number | null;
  inputProps: Omit<ComponentProps<'input'>, 'ref'> & {
    ref: MutableRefObject<HTMLInputElement | null>;
  };
  focus: () => void;
};
function stringToNumber(stringValue: string): number | null {
  const trimmedValue = stringValue.trim();
  const numberValue = trimmedValue !== '' ? Number(trimmedValue) : NaN;
  return isNaN(numberValue) ? null : numberValue;
}
function numberToString(numberValue: number | null): string {
  if (numberValue == null) {
    return '';
  }
  return StringUtils.padLeft(String(numberValue), 2);
}